import React from "react";
import {
  FaDatabase,
  FaChartBar,
  FaChartPie,
  FaCog,
  FaBell,
} from "react-icons/fa";
import {
  EntityType,
  parseEntityMentions,
} from "@/hooks/use-entity-mentions";

// ============================================================================
// Types
// ============================================================================

interface MessageTextProps {
  /** The text containing potential entity mentions */
  text: string;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const ENTITY_ICONS: Record<EntityType, React.ReactNode> = {
  tracker: <FaDatabase className="h-3 w-3" />,
  dashboard: <FaChartBar className="h-3 w-3" />,
  widget: <FaChartPie className="h-3 w-3" />,
  enrichment: <FaCog className="h-3 w-3" />,
  alert: <FaBell className="h-3 w-3" />,
};

const ENTITY_COLORS: Record<EntityType, string> = {
  tracker: "text-blue-400 bg-blue-500/15 border-blue-500/30",
  dashboard: "text-purple-400 bg-purple-500/15 border-purple-500/30",
  widget: "text-green-400 bg-green-500/15 border-green-500/30",
  enrichment: "text-orange-400 bg-orange-500/15 border-orange-500/30",
  alert: "text-red-400 bg-red-500/15 border-red-500/30",
};

// ============================================================================
// Component
// ============================================================================

/**
 * Renders text with entity mentions displayed as styled chips.
 * Converts `@[type:id:name]` format to inline chips showing just the name.
 */
export const MessageText: React.FC<MessageTextProps> = ({ text, className = "" }) => {
  if (!text) {
    return null;
  }

  const mentions = parseEntityMentions(text);

  // If no mentions, render plain text
  if (mentions.length === 0) {
    return <p className={`whitespace-pre-wrap ${className}`}>{text}</p>;
  }

  // Build array of text segments and mention chips
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  mentions.forEach((mention, index) => {
    // Add text before this mention
    if (mention.startIndex > lastIndex) {
      parts.push(
        <span key={`text-${index}`}>
          {text.slice(lastIndex, mention.startIndex)}
        </span>
      );
    }

    // Add the mention chip
    const colorClasses = ENTITY_COLORS[mention.type] || ENTITY_COLORS.tracker;
    const icon = ENTITY_ICONS[mention.type] || ENTITY_ICONS.tracker;

    parts.push(
      <span
        key={`mention-${index}`}
        className={`
          inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5
          rounded-md border text-xs font-medium
          ${colorClasses}
        `}
        title={`${mention.type}: ${mention.name}`}
      >
        {icon}
        <span>{mention.name}</span>
      </span>
    );

    lastIndex = mention.endIndex;
  });

  // Add remaining text after last mention
  if (lastIndex < text.length) {
    parts.push(
      <span key="text-end">{text.slice(lastIndex)}</span>
    );
  }

  return <p className={`whitespace-pre-wrap ${className}`}>{parts}</p>;
};

MessageText.displayName = "MessageText";

export default MessageText;
